import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Pin, PinOff, Plus, Trash2, X } from "lucide-react";
import type { ConvMeta } from "../lib/conversations.js";

interface Props {
  open: boolean;
  conversations: ConvMeta[];
  activeId: string | null;
  onClose: () => void;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onDelete: (id: string) => void;
  onLogout: () => void;
}

export function Sidebar({
  open,
  conversations,
  activeId,
  onClose,
  onSelect,
  onNew,
  onRename,
  onPin,
  onDelete,
  onLogout,
}: Props) {
  const [menuId, setMenuId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const editRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editId) editRef.current?.focus();
  }, [editId]);

  const pinned = conversations.filter((c) => c.pinned);
  const recent = conversations.filter((c) => !c.pinned);

  const relTime = (ts: number): string => {
    const s = Math.round((Date.now() - ts) / 1000);
    if (s < 60) return "just now";
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return d === 1 ? "yesterday" : `${d} days ago`;
  };

  function startRename(c: ConvMeta) {
    setMenuId(null);
    setEditId(c.id);
    setDraft(c.title);
  }
  function commitRename(id: string) {
    const t = draft.trim();
    if (t) onRename(id, t);
    setEditId(null);
  }

  const renderItem = (c: ConvMeta) => (
    <div key={c.id} className={`conv ${c.id === activeId ? "active" : ""}`}>
      {editId === c.id ? (
        <input
          ref={editRef}
          className="conv-edit"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename(c.id);
            if (e.key === "Escape") setEditId(null);
          }}
          onBlur={() => commitRename(c.id)}
        />
      ) : (
        <button className="conv-title" onClick={() => onSelect(c.id)} title={c.title}>
          <span className="conv-line">
            {c.pinned && <Pin className="pin-dot" size={11} />}
            <span className="conv-name">{c.title}</span>
          </span>
          <span className="conv-sub">{relTime(c.updatedAt)}</span>
        </button>
      )}
      <button className="conv-menu-btn" onClick={() => setMenuId(menuId === c.id ? null : c.id)} aria-label="Options">
        <MoreHorizontal size={16} />
      </button>
      {menuId === c.id && (
        <div className="conv-menu" onMouseLeave={() => setMenuId(null)}>
          <button onClick={() => { onPin(c.id, !c.pinned); setMenuId(null); }}>
            {c.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            {c.pinned ? "Unpin" : "Pin"}
          </button>
          <button onClick={() => startRename(c)}>
            <Pencil size={14} /> Rename
          </button>
          {c.id !== "telegram" && (
            <button
              className="danger"
              onClick={() => {
                setMenuId(null);
                if (confirm(`Delete "${c.title}"?`)) onDelete(c.id);
              }}
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} />}
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <div className="sidebar-head">
          <div className="brand-row">
            <span className="brand-mark" />
            <span className="brand-sm">ATLAS</span>
          </div>
          <button className="ghost icon close-x" onClick={onClose} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>
        <button className="new-chat" onClick={onNew}>
          <Plus size={16} /> New session
        </button>
        <div className="conv-list">
          {pinned.length > 0 && (
            <>
              <div className="conv-section">Pinned</div>
              {pinned.map(renderItem)}
            </>
          )}
          {recent.length > 0 && <div className="conv-section">Sessions</div>}
          {recent.map(renderItem)}
          {conversations.length === 0 && <div className="conv-empty">No sessions yet</div>}
        </div>
        <div className="sidebar-foot">
          <button onClick={onLogout}>Sign out</button>
        </div>
      </aside>
    </>
  );
}
